/**
 * ChatbotIconButton Component
 *
 * Icon-only chatbot button for headers and toolbars
 * Uses customer support context - no props needed for basic usage
 */

import React from 'react'
import { useCustomerSupport } from '../context/CustomerSupportContext'
import { IconBubbles } from '@innolab-trader/icons/action/IconBubbles'

export interface ChatbotIconButtonProps {
  /** Optional initial message to send to chatbot */
  initialMessage?: string
  /** Optional icon size (defaults to 16) */
  size?: number
  /** Optional additional CSS classes */
  className?: string
  /** Custom aria-label (defaults to "Need help") */
  ariaLabel?: string
  /** Custom title tooltip */
  title?: string
}

/**
 * ChatbotIconButton Component
 *
 * Self-contained icon button that opens the customer support chatbot
 * Gets openChatbot function from CustomerSupportProvider context
 *
 * @example
 * ```tsx
 * import { ChatbotIconButton } from '@innolab-trader/customer-support'
 *
 * function MyHeader() {
 *   return <ChatbotIconButton initialMessage="How can we help?" />
 * }
 * ```
 */
export const ChatbotIconButton: React.FC<ChatbotIconButtonProps> = ({
  initialMessage,
  size = 16,
  className = '',
  ariaLabel = 'Need help',
  title = 'Need help',
}) => {
  const customerSupport = useCustomerSupport()

  const handleClick = () => {
    if (!customerSupport) return
    customerSupport.openChatbot(initialMessage)
  }

  return (
    <button
      onClick={handleClick}
      className={`p-1.5 rounded hover:bg-hover text-muted-foreground hover:text-primary transition-colors ${className}`}
      aria-label={ariaLabel}
      title={title}
    >
      <IconBubbles size={size} />
    </button>
  )
}
